/* -----------------Globals--------------- */
import { useState } from "react";
import { Keyboard } from "react-native";

import { useUser } from "@clerk/clerk-expo";
import useAuthUser from "@hooks/useAuthUser";
import useClerkAuth from "@hooks/useClerkAuth";
import {
	formatError,
	isIncorrectPasscodeError,
	isNonActionableAuthError,
	isSignUpError,
	isTestModeEmail,
} from "@hooks/useClerkAuth/helpers";
import CustomSnackbar from "@memorang/ui/components/CustomSnackbar";
import Loader from "@memorang/ui/components/Loader";
import { useQuery } from "@tanstack/react-query";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import EmailView from "../components/EmailView";
import DialogContactSupport from "../components/dialogs/ContactSupportDialog";

type Props = {
	companyName: string;
	buildType: string;
	currentAppVersion: string;
	redirectUrl: string;
	handleTracking: (data: Record<string, string>) => void;
	supportEmail: string;
	emailFromUrl?: string;
	lastLoggedInEmail?: string;
	appId: string;
	tos?: string;
	privacy?: string;
	tenantId: string;
	onError?: (message: string) => void;
	logError: (error: unknown, title: string) => void;
};

const EmailContainer = ({
	companyName,
	handleTracking,
	supportEmail,
	buildType,
	currentAppVersion,
	redirectUrl,
	emailFromUrl,
	lastLoggedInEmail,
	appId,
	tenantId,
	tos,
	privacy,
	onError,
	logError,
}: Props) => {
	const router = useRouter();
	const [showContactSupportDialog, setShowContactSupportDialog] =
		useState(false);
	const [showInvalidDomainSnackBar, setShowInvalidDomainSnackBar] =
		useState(false);
	const [showPasscodeField, setShowPasscodeField] = useState(false);

	const { t } = useTranslation();

	const { user, isSignedIn } = useUser();
	const [email, setEmail] = useState<string | undefined>(undefined);
	const [isTestModeAuthInProgress, setIsTestModeAuthInProgress] =
		useState(false);
	const { userLogicalId } = useAuthUser();

	const { redeemCode } = useLocalSearchParams<{ redeemCode: string }>();

	useQuery({
		queryKey: ["verify-email", email],
		queryFn: async () => {
			const newUser = await user?.reload();
			return newUser ?? null;
		},
		enabled: !userLogicalId && isTestModeAuthInProgress,
		refetchInterval: 500,
	});

	const { authenticate, isLoading, isAuthInProgress } = useClerkAuth({
		appId,
		tenantId,
		redirectUrl,
		onEmailLinkVerify: (email: string) => {
			router.push(
				`/(auth)/verify?email=${email}${redeemCode ? `&redeemCode=${redeemCode}` : ""}`,
			);
		},
		onEmailCodeSuccess: () => {
			setIsTestModeAuthInProgress(true);
		},
	});

	if (isLoading) {
		return <Loader />;
	}

	const onChangeEmail = (email: string) => {
		if (isTestModeEmail(email)) {
			setShowPasscodeField(true);
		} else {
			setShowPasscodeField(false);
		}
	};

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const handleSubmit = async (email: string, passcode?: string) => {
		Keyboard.dismiss();
		setEmail(email);
		setIsTestModeAuthInProgress(isTestModeEmail(email));
		try {
			await authenticate(email, passcode);
		} catch (error: unknown) {
			if (isIncorrectPasscodeError(error)) {
				onError?.(t("auth.incorrectPasscode"));
				setIsTestModeAuthInProgress(false);
			} else {
				const isSignUp = isSignUpError(error);
				const longMessage = formatError(error);
				if (isNonActionableAuthError(error)) {
					onError?.(longMessage);
					return;
				}
				logError(
					longMessage,
					`Error during ${passcode ? "test mode " : ""}${isSignUp ? "sign-up" : "sign-in"}`,
				);
				setShowContactSupportDialog(true);
				handleTracking({
					eventName: isSignUp ? "mem_signup_attempted" : "mem_login_attempted",
					email,
				});
			}
		}
	};

	if (isSignedIn && userLogicalId) {
		return <Redirect href="/home" />;
	}

	return (
		<>
			<EmailView
				handleSubmit={handleSubmit}
				loading={isAuthInProgress || isTestModeAuthInProgress}
				emailFromUrl={emailFromUrl}
				lastLoggedInEmail={lastLoggedInEmail}
				tos={tos}
				privacy={privacy}
				showPasscodeField={showPasscodeField}
				onChangeEmail={onChangeEmail}
			/>
			{showContactSupportDialog && (
				<DialogContactSupport
					visible={showContactSupportDialog}
					companyName={companyName}
					supportEmail={supportEmail}
					buildType={buildType}
					currentAppVersion={currentAppVersion}
					handleDismiss={() => setShowContactSupportDialog(false)}
				/>
			)}
			{showInvalidDomainSnackBar && (
				<CustomSnackbar
					handleDismiss={() => setShowInvalidDomainSnackBar(false)}
					visible={showInvalidDomainSnackBar}
					duration={2000}
					message="You must use a permanent email address"
				/>
			)}
		</>
	);
};

export default EmailContainer;
